import { useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { ArrowDown2 } from "iconsax-react";
import type { BalanceSheetResponse } from "@/features/accounting/financial-reports/types";
import { formatReportAmount, formatDateRange } from "@/features/accounting/financial-reports/utils";
import { getAccountName } from "@/features/accounting/shared";
import { useLocale } from "@/shared/hooks";
import { Skeleton } from "@/shared/ui";

interface BalanceSheetCardProps {
  data?: BalanceSheetResponse;
  isLoading?: boolean;
  error?: unknown;
  className?: string;
  periodDates?: { start_date: string; end_date: string };
}

export default function BalanceSheetCard({ data, isLoading, error, className, periodDates }: BalanceSheetCardProps) {
  const { t } = useTranslation("FinancialReportPage");
  const locale = useLocale();
  const [isCurrentAssetsOpen, setIsCurrentAssetsOpen] = useState(true);
  const [isFixedAssetsOpen, setIsFixedAssetsOpen] = useState(true);

  if (isLoading) {
    return (
      <div className={cn("py-5 px-6 radius-3xl surface-base-fill", className)}>
        <Skeleton width={300} height={24} className="mb-4" />
        <div className="space-y-4">
          <Skeleton width="100%" height={200} />
          <Skeleton width="100%" height={100} />
          <Skeleton width="100%" height={100} />
          <Skeleton width="100%" height={60} />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cn("py-5 px-6 radius-3xl surface-base-fill", className)}>
        <h3 className="text-body-bold-lg content-base-primary mb-4">{t("balanceSheet.title")}</h3>
        <div className="text-body-regular-md content-action-neutral">
          {error ? t("balanceSheet.error") : t("balanceSheet.noData")}
        </div>
      </div>
    );
  }

  const periodLabel = periodDates
    ? formatDateRange(periodDates.start_date, periodDates.end_date)
    : `${data.as_of_date.slice(0, 4)} год`;
  const hasCurrentAssets = data.assets.current_assets.items.length > 0;
  const hasFixedAssets = data.assets.fixed_assets.items.length > 0;

  return (
    <div className={cn("py-5 px-6 radius-3xl surface-base-fill flex flex-col gap-5", className)}>
      <h3 className="text-body-bold-lg content-base-primary">{t("balanceSheet.title")}</h3>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center gap-5">
          <p className="text-label-md content-base-primary">{t("balanceSheet.assetsSection")}</p>
          <p className="text-label-sm content-base-low">{periodLabel}</p>
        </div>

        <div className="flex flex-col">
          {hasCurrentAssets && (
            <div>
              <div className="py-3 border-t surface-container-stroke">
                <div className="flex justify-between items-center gap-5">
                  <button
                    onClick={() => setIsCurrentAssetsOpen(!isCurrentAssetsOpen)}
                    className="flex justify-start items-center gap-1 cursor-pointer">
                    <span
                      className={cn(
                        "content-action-neutral transition-transform duration-200",
                        !isCurrentAssetsOpen && "-rotate-90",
                      )}>
                      <ArrowDown2 size={16} color="currentColor" />
                    </span>
                    <p className="text-body-regular-md content-action-neutral">
                      {data.assets.current_assets.title}
                    </p>
                  </button>
                  <p className="text-body-bold-lg content-base-primary text-right">
                    {formatReportAmount(data.assets.current_assets.total).text}
                  </p>
                </div>
              </div>

              {isCurrentAssetsOpen &&
                data.assets.current_assets.items.map((item) => (
                  <div key={item.code} className="py-3 border-t surface-container-stroke pl-6">
                    <div className="flex justify-between items-center gap-5">
                      <p className="text-body-regular-md content-action-neutral">
                        {getAccountName(item.code, locale)}
                      </p>
                      <p className="text-body-bold-md content-base-primary text-right">
                        {formatReportAmount(item.amount).text}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {hasFixedAssets && (
            <div>
              <div className="py-3 border-t surface-container-stroke">
                <div className="flex justify-between items-center gap-5">
                  <button
                    onClick={() => setIsFixedAssetsOpen(!isFixedAssetsOpen)}
                    className="flex justify-start items-center gap-1 cursor-pointer">
                    <span
                      className={cn(
                        "content-action-neutral transition-transform duration-200",
                        !isFixedAssetsOpen && "-rotate-90",
                      )}>
                      <ArrowDown2 size={16} color="currentColor" />
                    </span>
                    <p className="text-body-regular-md content-action-neutral">
                      {data.assets.fixed_assets.title}
                    </p>
                  </button>
                  <p className="text-body-bold-lg content-base-primary text-right">
                    {formatReportAmount(data.assets.fixed_assets.total).text}
                  </p>
                </div>
              </div>

              {isFixedAssetsOpen &&
                data.assets.fixed_assets.items.map((item) => (
                  <div key={item.code} className="py-3 border-t surface-container-stroke pl-6">
                    <div className="flex justify-between items-center gap-5">
                      <p className="text-body-regular-md content-action-neutral">
                        {getAccountName(item.code, locale)}
                      </p>
                      <p className="text-body-bold-md content-base-primary text-right">
                        {formatReportAmount(item.amount).text}
                      </p>
                    </div>
                  </div>
                ))}
            </div>
          )}

          <div className="py-3 border-t surface-container-stroke">
            <div className="flex justify-between items-center gap-5">
              <p className="text-body-bold-md content-action-neutral">{t("balanceSheet.totalAssetsLabel")}</p>
              <p className="text-body-bold-lg content-base-primary text-right">
                {formatReportAmount(data.assets.total).text}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center gap-5">
          <p className="text-label-md content-base-primary">{t("balanceSheet.liabilitiesSection")}</p>
          <p className="text-label-sm content-base-low">{periodLabel}</p>
        </div>

        <div className="flex flex-col">
          {data.liabilities.current_liabilities.items.map((item) => (
            <div key={item.code} className="py-3 border-t surface-container-stroke">
              <div className="flex justify-between items-center gap-5">
                <p className="text-body-regular-md content-action-neutral">
                  {item.name}
                </p>
                <p className="text-body-bold-lg content-base-primary text-right">
                  {formatReportAmount(item.amount).text}
                </p>
              </div>
            </div>
          ))}
          <div className="py-3 border-t surface-container-stroke">
            <div className="flex justify-between items-center gap-5">
              <p className="text-body-bold-md content-action-neutral">{t("balanceSheet.totalLiabilitiesLabel")}</p>
              <p className="text-body-bold-lg content-base-primary text-right">
                {formatReportAmount(data.liabilities.total).text}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center gap-5">
          <p className="text-label-md content-base-primary">{t("balanceSheet.equitySection")}</p>
          <p className="text-label-sm content-base-low">{periodLabel}</p>
        </div>

        <div className="flex flex-col">
          {data.equity.items.map((item) => (
            <div key={item.code} className="py-3 border-t surface-container-stroke">
              <div className="flex justify-between items-center gap-5">
                <p className="text-body-regular-md content-action-neutral">
                  {item.name}
                </p>
                <p className="text-body-bold-lg content-base-primary text-right">
                  {formatReportAmount(item.amount).text}
                </p>
              </div>
            </div>
          ))}
          <div className="py-3 border-t surface-container-stroke">
            <div className="flex justify-between items-center gap-5">
              <p className="text-body-bold-md content-action-neutral">{t("balanceSheet.totalEquityLabel")}</p>
              <p className="text-body-bold-lg content-base-primary text-right">
                {formatReportAmount(data.equity.total).text}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center gap-5 p-4 radius-md surface-component-fill">
          <p className="text-body-regular-md content-base-primary">{t("balanceSheet.passiveLabel")}</p>
          <p className="text-heading-lg content-base-primary text-right">
            {formatReportAmount(data.total_liabilities_and_equity).text}
          </p>
        </div>

        <p className="text-body-regular-sm content-base-low text-center">{t("balanceSheet.balanceEquation")}</p>
      </div>
    </div>
  );
}
